type AuthErrorLike = {
  code?: unknown;
  message?: unknown;
};

const RECAPTCHA_ERROR_CODES = new Set([
  'auth/captcha-check-failed',
  'auth/invalid-app-credential',
]);

const RECAPTCHA_ERROR_MESSAGES = [
  'recaptcha',
  'invalid application verifier',
  'error code: 39',
];

export const isRecaptchaVerificationError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const { code, message } = error as AuthErrorLike;

  if (typeof code === 'string' && RECAPTCHA_ERROR_CODES.has(code)) {
    return true;
  }

  if (typeof message !== 'string') {
    return false;
  }

  const normalizedMessage = message.toLowerCase();

  return RECAPTCHA_ERROR_MESSAGES.some((candidate) =>
    normalizedMessage.includes(candidate),
  );
};

type GrecaptchaWindow = Window &
  typeof globalThis & {
    grecaptcha?: {
      reset: (widgetId?: number) => void;
      execute: (widgetId?: number) => void | PromiseLike<string>;
    };
  };

export const resetRecaptchaWidget = (widgetId: number | null): boolean => {
  const grecaptcha = (window as GrecaptchaWindow).grecaptcha;

  if (widgetId === null || !grecaptcha) {
    return false;
  }

  try {
    grecaptcha.reset(widgetId);
    return true;
  } catch {
    return false;
  }
};

export const reopenRecaptchaWidget = (widgetId: number | null): boolean => {
  const grecaptcha = (window as GrecaptchaWindow).grecaptcha;

  if (widgetId === null || !grecaptcha) {
    return false;
  }

  try {
    grecaptcha.reset(widgetId);
    const execution = grecaptcha.execute(widgetId);
    execution?.then(undefined, () => undefined);
    return true;
  } catch {
    return false;
  }
};

type ClearableRecaptchaVerifier = {
  clear: () => void;
};

export const clearRecaptchaVerifier = (
  verifier: ClearableRecaptchaVerifier | null,
  container: HTMLElement | null,
): void => {
  try {
    verifier?.clear();
  } catch {
    // Cleanup must remain safe if Firebase already destroyed the verifier.
  }

  // Firebase does not remove DOM for invisible widgets when clear() is called.
  container?.replaceChildren();
};
